import React, { useMemo, useRef, useState } from 'react';
import Modal from '../modal/Modal';
import {
    countRetroSchedules,
    diffDateKeys,
    formatKstDate,
    getKstTodayKey,
    OpenPause,
    previewChangeEnd,
    PauseLike,
} from '@/features/membershipPause/status';
import { PausePassKind } from '@/features/membershipPause/types';

export type PauseDateFormMode = 'open' | 'changeEnd';

export type PauseDateFormValue = {
    startDate: string;
    endDate: string | null;
    /** 소급 시작이면 그 기간 수업 수(경고용) */
    retroCount: number;
};

type Props = {
    mode: PauseDateFormMode;
    kind: PausePassKind;
    /** 끝나는 날 변경일 때 지금 열린 정지 */
    open?: OpenPause | null;
    pause?: PauseLike;
    expiry?: string | null;
    schedules?: any[] | null;
    isSubmitting: boolean;
    onSubmit: (value: PauseDateFormValue) => void;
    onClose: () => void;
};

const inputClass =
    'h-[40px] w-full rounded-[8px] border border-[#D1D5DB] px-[12px] text-[14px] text-[#111827] focus:border-[#3D73DD] focus:outline-none disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]';

/**
 * 정지 등록 / 해제일 바꾸기 입력 창.
 * - 해제일은 "그날부터 다시 수업할 수 있는 날"이다(서버: 끝나는 날 0시 제외).
 * - 시작일은 지난 날짜도 고를 수 있다(소급). 그 기간 수업 수는 제출 때 한 번 경고한다.
 */
const ModalPauseDateForm = ({ mode, kind, open, pause, expiry, schedules, isSubmitting, onSubmit, onClose }: Props) => {
    const todayKey = getKstTodayKey();
    const [startDate, setStartDate] = useState<string>(mode === 'changeEnd' && open ? open.startKey : todayKey);
    const [noEnd, setNoEnd] = useState<boolean>(mode === 'changeEnd' ? !open?.endKey : false);
    const [endDate, setEndDate] = useState<string>(
        mode === 'changeEnd' && open?.endKey ? open.endKey : '',
    );

    const isRetro = mode === 'open' && !!startDate && startDate < todayKey;
    const retroCount = useMemo(
        () => (isRetro ? countRetroSchedules(kind, schedules, startDate, todayKey) : 0),
        [isRetro, kind, schedules, startDate, todayKey],
    );

    const effectiveEnd = noEnd ? null : endDate || null;
    const error = (() => {
        if (!startDate) return '시작일을 골라 주세요.';
        if (!noEnd && !endDate) return '해제일을 고르거나 「해제일 없음」을 켜 주세요.';
        if (effectiveEnd && effectiveEnd <= startDate) return '해제일은 시작일보다 뒤여야 해요.';
        return null;
    })();

    const changePreview =
        mode === 'changeEnd' ? previewChangeEnd(pause, effectiveEnd, expiry ?? null, todayKey) : null;
    const pausedDays = effectiveEnd && startDate ? diffDateKeys(startDate, effectiveEnd) : null;

    // 입력 전부터 빨간 문구를 띄우지 않는다 — 해제일 칸·「해제일 없음」을 건드렸거나 제출을 시도한 뒤에만.
    const [isEndTouched, setIsEndTouched] = useState(false);
    const [isSubmitTried, setIsSubmitTried] = useState(false);
    const visibleError = isEndTouched || isSubmitTried ? error : null;

    const submit = () => {
        setIsSubmitTried(true);
        if (error || isSubmitting) return;
        onSubmit({ startDate, endDate: effectiveEnd, retroCount });
    };

    // 공용 Modal 은 처음 onClose 를 붙잡는다 — 처리 중 Escape 가 처음 닫기 함수로 창을 닫지 않게 최신 값을 읽는다.
    const closeRef = useRef<() => void>(() => undefined);
    closeRef.current = () => {
        if (!isSubmitting) onClose();
    };
    const stableClose = useRef(() => closeRef.current()).current;

    return (
        <Modal whiteViewWidth={440} onClose={stableClose} isCloseOutside={false} isWhiteOverFlowScroll={false}>
            <div className="flex flex-col px-[28px] pt-[28px] pb-[24px]">
                <div className="text-[18px] font-bold text-[#1F2937]">{mode === 'open' ? '수강권 정지' : '해제일 바꾸기'}</div>
                <div className="mt-[6px] text-[13px] leading-[20px] text-[#6B7280]">
                    정지 중에는 회원이 앱에서 수업을 신청·예약할 수 없어요. 해제하면 정지한 일수만큼 만료일이 늘어나요.
                </div>

                <label className="mt-[20px] text-[13px] font-semibold text-[#374151]">시작일</label>
                <input
                    type="date"
                    className={`${inputClass} mt-[6px]`}
                    value={startDate}
                    disabled={mode === 'changeEnd' || isSubmitting}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                {isRetro && (
                    <div className="mt-[6px] text-[12px] text-[#B45309]">지난 날짜부터 정지해요. 해제하면 이 날부터 센 일수만큼 만료일이 늘어나요.</div>
                )}

                <div className="mt-[16px] flex flex-row items-center justify-between">
                    <label className="text-[13px] font-semibold text-[#374151]">해제일</label>
                    <label className="flex cursor-pointer flex-row items-center gap-[6px] text-[13px] text-[#374151]">
                        <input
                            type="checkbox"
                            checked={noEnd}
                            disabled={isSubmitting}
                            onChange={(e) => {
                                setIsEndTouched(true);
                                setNoEnd(e.target.checked);
                            }}
                        />
                        해제일 없음
                    </label>
                </div>
                <input
                    type="date"
                    className={`${inputClass} mt-[6px]`}
                    value={noEnd ? '' : endDate}
                    min={startDate || undefined}
                    disabled={noEnd || isSubmitting}
                    onChange={(e) => {
                        setIsEndTouched(true);
                        setEndDate(e.target.value);
                    }}
                    onBlur={() => setIsEndTouched(true)}
                />
                <div className="mt-[6px] text-[12px] text-[#6B7280]">
                    {noEnd
                        ? '직접 해제할 때까지 정지가 이어져요.'
                        : '해제일 0시에 자동으로 풀려요. 해제일부터 다시 수업할 수 있어요.'}
                    {pausedDays && pausedDays > 0 ? ` (정지 ${pausedDays}일)` : ''}
                </div>

                {changePreview && (
                    <div className="mt-[12px] rounded-[8px] bg-[#EFF6FF] px-[12px] py-[10px] text-[13px] text-[#1E3A8A]">
                        {changePreview.days > 0
                            ? `이 날짜로 바로 해제돼요. 만료일이 ${changePreview.days}일 늘어나요${
                                  changePreview.expiryBefore && changePreview.expiryAfter
                                      ? ` (${formatKstDate(changePreview.expiryBefore)} → ${formatKstDate(changePreview.expiryAfter)}, 예상)`
                                      : ''
                              }.`
                            : '이 날짜로 바로 해제돼요. 만료일은 그대로예요.'}
                    </div>
                )}

                {visibleError && <div className="mt-[12px] text-[12px] text-[#DC2626]">{visibleError}</div>}

                <div className="mt-[20px] flex flex-row justify-end gap-[8px]">
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={onClose}
                        className="h-[40px] rounded-[8px] border border-[#D1D5DB] bg-white px-[16px] text-[14px] font-semibold text-[#374151] disabled:opacity-50"
                    >
                        닫기
                    </button>
                    <button
                        type="button"
                        disabled={!!error || isSubmitting}
                        onClick={submit}
                        className="h-[40px] rounded-[8px] bg-[#3D73DD] px-[16px] text-[14px] font-semibold text-white disabled:opacity-50"
                    >
                        {isSubmitting ? '처리 중' : mode === 'open' ? '정지하기' : '바꾸기'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ModalPauseDateForm;
